import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { createScopedJSONStorage, scopedLocalStorage } from '@/lib/browser/scopedStorage';
import type { PublicationSearchCriteriaDto } from '@/api/ApiClient';

const STORAGE_KEY = 'publicationFilters';
export const COLUMN_VISIBILITY_STORAGE_KEY = 'publicationColumnVisibility';

export const defaultPublicationFilterCriteria: PublicationSearchCriteriaDto = {
  materials: [],
  elements: [],
  searchText: '',
  publicationType: undefined,
  scienceDriver: undefined,
};

function valueDiffers(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return true;
    return a.some((v, i) => v !== b[i]);
  }
  return a !== b;
}

/** Indicates whether non-basic publication filters are set (for Filter badge dot). */
export function hasPublicationExtendedFiltersSet(filters: PublicationSearchCriteriaDto): boolean {
  const def = defaultPublicationFilterCriteria;
  if (valueDiffers(filters.materials ?? [], def.materials ?? [])) return true;
  if (valueDiffers(filters.elements ?? [], def.elements ?? [])) return true;
  if (valueDiffers(filters.publicationType, def.publicationType)) return true;
  if (valueDiffers(filters.scienceDriver, def.scienceDriver)) return true;
  return false;
}

export const defaultPublicationColumnVisibilityModel: GridColumnVisibilityModel = {
  authors: true,
  title: true,
  journal: true,
  year: true,
  materials: true,
  projectType: true,
  scienceDriver: true,
};

export function loadStoredPublicationColumnVisibility(): GridColumnVisibilityModel {
  if (typeof window === 'undefined') return defaultPublicationColumnVisibilityModel;
  try {
    const stored = scopedLocalStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (!stored) return defaultPublicationColumnVisibilityModel;
    return { ...defaultPublicationColumnVisibilityModel, ...(JSON.parse(stored) as GridColumnVisibilityModel) };
  } catch {
    return defaultPublicationColumnVisibilityModel;
  }
}

interface PublicationFilterStore {
  filters: PublicationSearchCriteriaDto;
  isSearching: boolean;
  setIsSearching: (isSearching: boolean) => void;
  setFilters: (filters: PublicationSearchCriteriaDto) => void;
  resetFilters: () => void;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: (
    model:
      | { page: number; pageSize: number }
      | ((prev: { page: number; pageSize: number }) => { page: number; pageSize: number })
  ) => void;
}

export const usePublicationFilterStore = create<PublicationFilterStore>()(
  persist(
    (set, get) => ({
      filters: defaultPublicationFilterCriteria,
      isSearching: false,
      setIsSearching: (isSearching) => set({ isSearching }),
      setFilters: (filters) => set({ filters }),
      resetFilters: () => set({ filters: defaultPublicationFilterCriteria }),
      paginationModel: { page: 0, pageSize: 25 },
      setPaginationModel: (modelOrUpdater) => {
        const current = get().paginationModel;
        const newModel = typeof modelOrUpdater === 'function' ? modelOrUpdater(current) : modelOrUpdater;
        set({ paginationModel: newModel });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createScopedJSONStorage(),
      partialize: (state) => ({
        filters: state.filters,
        paginationModel: state.paginationModel,
      }),
    }
  )
);
